import { Select } from 'antd';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import React, { useEffect, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import Typo from 'consts/typography';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from 'components/Form/FormField';
import walletApi from 'api/walletApi';
import SuccessIcon from 'assets/icons/SuccessIcon';
import { BiArrowBack } from 'react-icons/bi';
import { showSuccessModal } from 'components/Modals/CommonModals';

interface AddUserBankbookProps {
  onBack: () => void;
}

type FormInputs = {
  bankbookName: string;
  disbursementNumber?: string;
  branchTransitNumber?: string;
  routingNumber?: string;
  accountNumber?: string;
};

const transactionTypes = [
  {
    value: 'CARD',
    label: 'Card',
  },
  {
    value: 'ACH',
    label: 'ACH',
  },
];

const AddUserBankbook: React.FunctionComponent<AddUserBankbookProps> = ({ onBack }) => {
  const t = useAppTranslation();
  const et = useErrTranslation();
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const [currentType, setCurrentType] = useState('ACH');
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handleResponseError = useHandleResponseError();

  const validationSchema = yup.object({
    bankbookName: yup
      .string()
      .required(et('error.validate.field.name.required'))
      .max(50, et('error.validate.field.name.invalid')),
    disbursementNumber: yup.string().when(() => {
      if (currentType === 'CARD') {
        return yup
          .string()
          .required(et('error.validate.field.disbursementNumber.required'))
          .min(15, et('error.validate.field.disbursementNumber.invalid'))
          .max(19, et('error.validate.field.disbursementNumber.invalid'))
          .matches(/^[0-9]+$/, et('error.validate.field.disbursementNumber.invalid'));
      }
      return yup.string().nullable();
    }),
    branchTransitNumber: yup.string().when(() => {
      if (currentType !== 'CARD') {
        return yup
          .string()
          .required(et('error.validate.field.branchTransitNumber.required'))
          .length(9, et('error.validate.field.branchTransitNumber.invalid'))
          .matches(/^[0-9]+$/, et('error.validate.field.branchTransitNumber.invalid'));
      }
      return yup.string().nullable();
    }),
    routingNumber: yup.string().when(() => {
      if (currentType !== 'CARD') {
        return yup
          .string()
          .required(et('error.validate.field.routingNumber.required'))
          .length(9, et('error.validate.field.routingNumber.invalid'))
          .matches(/^[0-9]+$/, et('error.validate.field.routingNumber.invalid'));
      }
      return yup.string().nullable();
    }),
    accountNumber: yup.string().when(() => {
      if (currentType !== 'CARD') {
        return yup
          .string()
          .required(et('error.validate.field.accountNumber.required'))
          .min(5, et('error.validate.field.accountNumber.invalid'))
          .max(17, et('error.validate.field.accountNumber.invalid'))
          .matches(/^[0-9]+$/, et('error.validate.field.accountNumber.invalid'));
      }
      return yup.string().nullable();
    }),
  });

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      bankbookName: '',
      disbursementNumber: '',
      accountNumber: '',
      branchTransitNumber: '',
      routingNumber: '',
    },
  });

  const onSubmit = async (data: FormInputs) => {
    setIsLoading(true);
    const { ok, error } = await walletApi.addUserBankbooks({
      name: data.bankbookName,
      type: currentType,
      disbursementNumber: data.disbursementNumber || undefined,
      routingNumber: data.routingNumber || undefined,
      branchTransitNumber: data.branchTransitNumber || undefined,
      accountNumber: data.accountNumber || undefined,
    });
    setIsLoading(false);
    if (ok) {
      showSuccessModal({
        title: t('userBankbook.success.title'),
        content: t('userBankbook.success.message'),
        onOk: onClose(),
      });
    }
    if (error) handleResponseError(error);
  };

  const onTypeChange = (type: string) => {
    setCurrentType(type);
    setValue('disbursementNumber', '', {
      shouldValidate: true,
    });
    setValue('accountNumber', '', {
      shouldValidate: true,
    });
    setValue('branchTransitNumber', '', {
      shouldValidate: true,
    });
    setValue('routingNumber', '', {
      shouldValidate: true,
    });
  };

  const onChangeInputNumber = (
    input: 'disbursementNumber' | 'routingNumber' | 'branchTransitNumber' | 'accountNumber',
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const regex = /^[\d]+$/;
    const value = event.target.value;

    if (regex.test(value) || value === '') {
      setValue(input, event.target.value, {
        shouldValidate: true,
      });
    }
  };

  const onClose = () => {
    onBack();
  };

  useEffect(() => {
    reset({
      bankbookName: '',
      disbursementNumber: '',
      accountNumber: '',
      branchTransitNumber: '',
      routingNumber: '',
    });
  }, [isShowSuccess]);

  return (
    <>
      {!isShowSuccess ? (
        <form className="center-content-col gap-2 primary-text-color  add-user-bank">
          <div className="w-90 flex items-center cursor-pointer back-btn" onClick={onClose}>
            <BiArrowBack size={20} />
            <span className={`primary-text-color ${isPhone ? Typo.p4 : Typo.p2}`}>
              {t('header.back')}
            </span>
          </div>
          <div className="form-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="bankbookName"
            >
              {t('userProfile.rowInfo.name')}
            </label>
            <FormField
              control={control}
              errors={errors}
              placeholder={t('placeholder.enterName')}
              name="bankbookName"
              component="input"
              type="text"
              isAboveError={false}
              maxLength={50}
            />
          </div>
          <div className="form-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('cashier.transactionType')}
            </label>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentType}
              onChange={onTypeChange}
              options={transactionTypes.map((type) => ({
                value: type.value,
                label: (
                  <span
                    className={`primary-text-color ${
                      isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                    }`}
                  >
                    {type.label}
                  </span>
                ),
              }))}
            />
          </div>
          {currentType === 'CARD' && (
            <div className="form-input">
              <label
                className={`${
                  isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                } font-medium primary-text-color`}
                htmlFor="disbursementNumber"
              >
                {t('cashier.disbursementNumber')}
              </label>
              <FormField
                control={control}
                errors={errors}
                placeholder={t('placeholder.enterDisbursementNumber')}
                name="disbursementNumber"
                component="input"
                type="text"
                isAboveError={false}
                maxLength={19}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeInputNumber('disbursementNumber', e)
                }
              />
            </div>
          )}
          {currentType !== 'CARD' && (
            <>
              <div className="form-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="routingNumber"
                >
                  {t('cashier.routingNumber')}
                </label>
                <FormField
                  control={control}
                  errors={errors}
                  placeholder={t('placeholder.enterRoutingNumber')}
                  name="routingNumber"
                  component="input"
                  type="text"
                  isAboveError={false}
                  maxLength={9}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeInputNumber('routingNumber', e)
                  }
                />
              </div>
              <div className="form-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="branchTransitNumber"
                >
                  {t('cashier.branchTransitNumber')}
                </label>
                <FormField
                  control={control}
                  errors={errors}
                  placeholder={t('placeholder.enterBranchTransitNumber')}
                  name="branchTransitNumber"
                  component="input"
                  type="text"
                  isAboveError={false}
                  maxLength={9}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeInputNumber('branchTransitNumber', e)
                  }
                />
              </div>
              <div className="form-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="accountNumber"
                >
                  {t('cashier.accountNumber')}
                </label>
                <FormField
                  control={control}
                  errors={errors}
                  placeholder={t('placeholder.enterAccountNumber')}
                  name="accountNumber"
                  component="input"
                  type="text"
                  isAboveError={false}
                  maxLength={17}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeInputNumber('accountNumber', e)
                  }
                />
              </div>
            </>
          )}
          <button
            // disabled={isLoading || isDirty || !isValid}
            className={`mt-2 button-generate ${isLoading || !isValid ? 'disabled' : ''} add-user-bank__btn`}
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            {t('userBankbook.addNewBankbook')}
          </button>
        </form>
      ) : (
        <div className="center-content-col gap-3 withdraw--success text-center">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('userBankbook.success.title')}</p>
          <p className="message">{`${t('userBankbook.success.message')}`}</p>
          <button
            type="button"
            className=" mt-2 button-generate add-user-bank__btn"
            onClick={onClose}
          >
            {t('withdraw.close')}
          </button>
        </div>
      )}
    </>
  );
};

export default AddUserBankbook;
